import React, { useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { useNavigate, useParams } from 'react-router-dom';
import { post } from 'aws-amplify/api';
import '../styles/management.css';

function CreateInvitationScreen() {
  const [formData, setFormData] = useState({ password: '' });
  const [guests, setGuests] = useState([{ email: '', phone: '', firstName: '', shortName: '', lastName: '', sendInvitation: true, contactMethod: 'email' }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const { eventId } = useParams();
  const navigate = useNavigate();
  
  const addGuest = () => {
    setGuests([...guests, { email: '', phone: '', firstName: '', shortName: '', lastName: '', sendInvitation: true, contactMethod: 'email' }]);
  };

  const removeGuest = (index) => {
    const updatedGuests = [...guests];
    updatedGuests.splice(index, 1);
    setGuests(updatedGuests);
  };

  const updateGuest = (index, field, value) => {
    if (!isSubmitting) {
      const updatedGuests = [...guests];
      updatedGuests[index][field] = value;
      setGuests(updatedGuests);
    }
  };

  const copyQRCode = async () => {
    if (qrCode) {
      await navigator.clipboard.writeText(qrCode);
      window.alert('QR code copied to clipboard');
    }
  };

  const handleCreateInvitation = async (e) => {
    if (e) e.preventDefault();
    
    // Validate if guests have at least one name filled out
    const invalidGuestNames = guests.filter(guest => 
      !guest.firstName && !guest.shortName && !guest.lastName
    );
    
    if (invalidGuestNames.length > 0) {
      setError('All guests must have at least one name set');
      return;
    }
    
    // Validate if guests receiving invitations have required contact information
    const invalidGuestContact = guests.filter(guest => 
      guest.sendInvitation && (
        (guest.contactMethod === 'email' && !guest.email.trim()) ||
        (guest.contactMethod === 'phone' && !guest.phone.trim())
      )
    );
    
    if (invalidGuestContact.length > 0) {
      setError('All guests receiving invitations must have their chosen contact method filled out');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    
    try {
      const restOperation = post({
        apiName: 'events',
        path: `/invitations/${eventId}`,
        options: {
          body: { 
            event_id: eventId,
            password: formData.password || undefined,  // Only include if password is set
            guests: guests.map(guest => ({
              email: guest.email.trim(),
              phone: guest.phone.trim(),
              firstName: guest.firstName.trim(),
              shortName: guest.shortName.trim(),
              lastName: guest.lastName.trim(),
              sendInvitation: guest.sendInvitation
            }))
          }
        }
      });
      

      await restOperation.response;
      const { body } = await restOperation.response;
      const data = await body.json();
      console.log('Invitation created:', data);
      
      // Store the QR code for display
      setQrCode(data.qrCode);
      setIsSubmitting(false);

      // Navigate back to new invitation
      // TODO work out how to update the states, or just force reload? { state: { newGuest: json } }
      navigate(`/invitations/${eventId}`);

    } catch (error) {
      console.error('Error creating invitation:', error);
      setError('Unable to create invitation. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (qrCode) {
    return (
      <div className="content">
        <h1 className="content__heading">Invitation Created!</h1>
        <div className="qr-code-container">
          <h2>QR Code:</h2>
          <div className="qr-code">
            <p>{qrCode}</p>
            <button className="button button--secondary" onClick={copyQRCode}>
              Copy QR Code
            </button>
          </div>
          <p className="qr-instructions">
            This QR code can be shared with all invited guests.
            They can use this code to check in at the event.
          </p>
        </div>
        <button className="button button--primary" onClick={() => navigate(`/invitations/${eventId}`)}>
          Done
        </button>
      </div>
    );
  }

  return (
    <div className="content">
      <h1 className="content__heading">Create Invitation</h1>
      
      {isSubmitting && (
        <LoadingSpinner message="Creating invitation..." />
      )}
      
      {error && (
        <div className="message message--error">
          {error}
        </div>
      )}

      <form onSubmit={handleCreateInvitation} className="form">
        {guests.map((guest, index) => (
          <div key={index} className="guest">
            <h3>Guest {index + 1}</h3>

            <div className="form__group">
              <label htmlFor="password">Password Protection (Optional)</label>
              <input
                type="password"
                className="form__input"
                id="password"
                value={formData.password ? '************' : ''}
                onChange={(e) => !isSubmitting && setFormData({...formData, password: e.target.value})}
                onFocus={(e) => e.target.value = formData.password}
                onBlur={(e) => e.target.value = formData.password ? '************' : ''}
                placeholder="Leave empty for no password"
                minLength="8"
                pattern={formData.password ? ".{8,}" : ".*"}
                title="Password must be at least 8 characters if set"
                disabled={isSubmitting}
              />
            </div>

            <div className="form__group">
              <label>First Name (optional, at least one name required)</label>
              <input
                type="text"
                className="form__input"
                placeholder="First Name"
                value={guest.firstName}
                onChange={(e) => updateGuest(index, 'firstName', e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="form__group">
              <label>Short Name (optional, at least one name required)</label>
              <input
                type="text"
                className="form__input"
                placeholder="Short Name"
                value={guest.shortName}
                onChange={(e) => updateGuest(index, 'shortName', e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="form__group">
              <label>Last Name (optional, at least one name required)</label>
              <input
                type="text"
                className="form__input"
                placeholder="Last Name"
                value={guest.lastName}
                onChange={(e) => updateGuest(index, 'lastName', e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="form__group">
              <label>Contact Method</label>
              <div>
                <label style={{marginRight: '1rem'}}>
                  <input
                    type="radio"
                    name={`contactMethod-${index}`}
                    value="email"
                    checked={guest.contactMethod === 'email'}
                    onChange={(e) => updateGuest(index, 'contactMethod', e.target.value)}
                    style={{marginRight: '0.5rem'}}
                    disabled={isSubmitting}
                  />
                  Email
                </label>
                <label>
                  <input
                    type="radio"
                    name={`contactMethod-${index}`}
                    value="phone"
                    checked={guest.contactMethod === 'phone'}
                    onChange={(e) => updateGuest(index, 'contactMethod', e.target.value)}
                    style={{marginRight: '0.5rem'}}
                    disabled={isSubmitting}
                  />
                  Phone
                </label>
              </div>
            </div>

            <div className="form__group">
              <label>Email Address {guest.sendInvitation && guest.contactMethod === 'email' ? '(required)' : '(optional)'}</label>
              <input
                type="email"
                className="form__input"
                placeholder="Email Address"
                value={guest.email}
                onChange={(e) => updateGuest(index, 'email', e.target.value)}
                required={guest.sendInvitation && guest.contactMethod === 'email'}
                disabled={isSubmitting}
              />
            </div>

            <div className="form__group">
              <label>Phone Number {guest.sendInvitation && guest.contactMethod === 'phone' ? '(required)' : '(optional)'}</label>
              <input
                type="tel"
                className="form__input"
                placeholder="Phone Number"
                value={guest.phone}
                onChange={(e) => updateGuest(index, 'phone', e.target.value)}
                required={guest.sendInvitation && guest.contactMethod === 'phone'}
                disabled={isSubmitting}
              />
            </div>
            
            <div className="form__group">
              <label className="form__checkbox-label">
                <input
                  type="checkbox"
                  className="form__checkbox"
                  checked={guest.sendInvitation}
                  onChange={(e) => updateGuest(index, 'sendInvitation', e.target.checked)}
                  disabled={isSubmitting}
                />
                Send invitation via {guest.contactMethod}?
              </label>
            </div>
            
            {guests.length > 1 && (
              <button
                type="button" 
                className="button button--danger"
                onClick={() => removeGuest(index)}
                disabled={isSubmitting}
              >
                Remove Guest
              </button>
            )}
          </div>
        ))}
        
        <div className="actions">
          <button 
            type="button" 
            className="button button--secondary"
            onClick={addGuest}
            disabled={isSubmitting}
          >
            Add Another Guest
          </button>
          
          <button
            type="submit"
            className="button button--primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Invitation'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateInvitationScreen;